/**
 * Which side-navigation row is the current one (#15618).
 *
 * The longest entry href that the pathname starts with AT A SEGMENT BOUNDARY
 * wins — the prototype's `activeNavId` (`schoolai-design-sandbox`,
 * `src/chrome/AppFrame.tsx`). Longest, so a nested destination lights only
 * its own row; segment-bounded, so `/plan` never lights on `/plans` and
 * `/handbook` never on a sibling that shares its prefix.
 *
 * The aliases are the extra routes the header's `isEntryActive`
 * (`header-nav-entries.tsx`) already counts as an entry's surface — Notes
 * owns `/events` — so
 * the two navs agree about where the viewer is.
 *
 * Pure and client-safe.
 */

import { ENTRIES, type EntryKey } from '@/lib/org-nav/vocabulary'

const ALIASES: Partial<Record<EntryKey, readonly string[]>> = {
  notes: ['/events'],
}

function matchesAt(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`)
}

/** The active entry among `keys` (the rows actually drawn), or `null`. */
export function activeNavKey(
  pathname: string,
  keys: readonly EntryKey[],
): EntryKey | null {
  let best: { key: EntryKey; length: number } | null = null
  for (const key of keys) {
    const href = ENTRIES[key].href
    if (href === null) continue
    for (const candidate of [href, ...(ALIASES[key] ?? [])]) {
      if (
        matchesAt(pathname, candidate) &&
        (best === null || candidate.length > best.length)
      ) {
        best = { key, length: candidate.length }
      }
    }
  }
  return best?.key ?? null
}
